/**
 * Enums which are shared between the server and client.
 */

//#region App enums

export enum Brokerage {
    PUBLIC = "PUBLIC",
    WEBULL = "WEBULL",
    WEBULL_CANADA = "WEBULL_CANADA",
    PAPER = "PAPER",
    UNSUPPORTED = "UNSUPPORTED"
}

export enum Grant {
    TRADER = "TRADER",
    DISCORD_ALERTS = "DISCORD_ALERTS"
}

export enum ValueDisplayType {
    DOLLAR = "DOLLAR",
    PERCENT = "PERCENT"
}

//#endregion

//#region Websocket enums

/** Available topics for websocket messages, used for routing to relevant handlers */
export enum WsRoute {
    SYS = "SYS",
    BALANCES = "BALANCES",
    POSITIONS = "POSITIONS",
    ORDERS = "ORDERS",
    QUOTES = "QUOTES",
}

export enum WsSysCommand {
    /** @To-Server Auth message to connect WebSocket. */
    AUTH = "AUTH",
    UPDATE_USER_DATA = "UPDATE_USER_DATA",
    /** @To-Server Get current Discord alert enabled status.  
     * Payload: n/a
     * @To-Client Transmits current Discord alert enabled status.  
     * Payload: boolean
     */
    DISCORD_GET_ALERT_STATUS = "DISCORD_GET_ALERT_STATUS",
    /** @To-Server Sets DiscordBot's subscription parameters for the user.  
     * Payload: {enabled: boolean}
     */
    DISCORD_TOGGLE_ALERTS = "DISCORD_TOGGLE_ALERTS",
    /** @To-Client Scanner tracker update push.
     * Payload: ScannerTradeWsUpdate[] */
    SCANNER_ALERT_UPDATE = "SCANNER_ALERT_UPDATE",
}

export enum WsBalancesCommand {
    /** @To-Server Request current balances.
     * Payload: n/a. */
    GET = "GET",
    /** @To-Client Transmits balances update.
     * Payload: Partial<Balances> (only changed fields). */
    UPDATE = "UPDATE",
}

export enum WsPositionsCommand {
    /** @To-Server Request all positions.
     * Payload: n/a. */
    GET_ALL = "GET_ALL",
    /** @To-Client Transmits position update (new or modified).
     * Payload: Partial<Position> with 'id' field set to symbol. */
    UPDATE = "UPDATE",
    /** @To-Client Transmits position removal.
     * Payload: { id: string } (symbol). */
    REMOVE = "REMOVE",
    /** @To-Server Transmits signal to cancel all orders and liquidate held contracts
     * Payload: Position ID
     */
    FLATTEN = "FLATTEN",
}

export enum WsQuoteCommand {
    /** @To-Server
     * Request quotes for an array of instruments.  
     * Payload: Instrument[].  
     * May send empty array to cancel stream. */
    STREAM_INSTRUMENTS = "STREAM_INSTRUMENTS",
    /** @To-Server Request option expirations for a given symbol.  
     * Payload: symbol
     * @To-Client Transmit requested expirations.  
     * Payload: OperationResult [success, data?, error?]. */
    GET_EXPIRIES = "GET_EXPIRIES",
    /** @To-Server Request option chain stream.  
     * Payload: [symbol, expiry]. */
    STREAM_CHAIN = "STREAM_CHAIN",
    /** @To-Server Cancel option chain stream.  
     * Payload: n/a. */
    UNSTREAM_CHAIN = "UNSTREAM_CHAIN",
    /** @To-Client Transmit requested instrument quotes.  
     * Payload: Quote[]. */
    HANDLE_INSTRUMENTS = "HANDLE_INSTRUMENTS",
    /** @To-Client Transmit requested option chain.  
     * Payload: OptionChain. */
    HANDLE_CHAIN = "HANDLE_CHAIN",
}

export enum WsOrdersCommand {
    /** @To-Server Request all orders.
     * Payload: n/a
     * @To-Client Response with all orders (full data on first request).
     * Payload: Order[] */
    GET_ALL = "GET_ALL",
    /** @To-Client Transmits order update (delta).
     * Payload: Partial<Order> with only changed fields */
    UPDATE = "UPDATE",
    /** @To-Server Place an order.  
     * Payload: Order
     * @To-Client Confirm result.  
     * Payload: OperationResult [success, error?] */
    PLACE = "PLACE",
    /** @To-Server Cancel an order.  
     * Payload: orderId
     * @To-Client Confirm result.  
     * Payload: OperationResult [success, error?] */
    CANCEL = "CANCEL",
    /** @To-Server Modify an order.  
     * Payload: Order.
     * @To-Client Confirm result.  
     * Payload: OperationResult [success, error?] */
    MODIFY = "MODIFY",
    /** @To-Server Remove a completed order.  
     * Payload: orderId
     * @To-Client Confirm result.  
     * Payload: OperationResult [success, error?] */
    REMOVE = "REMOVE",
}

//#endregion

//#region Order enums

/** Type of security, derived from Snaptrade values. */
export enum SecurityType {
    ADR = "ADR",
    BND = "Bond",
    CS = "Common Stock",
    CEF = "Closed End Fund",
    CRYPTO = "Cryptocurrency",
    ET = "ETF",
    OEF = "Open Ended Fund",
    PM = "Precious Metals",
    PS = "Preferred Stock",
    RT = "Right",
    STRUCT = "Structured Product",
    UT = "Unit",
    WI = "When Issued",
    WT = "Warrant"
}

export enum OptionType {
    CALL = "CALL",
    PUT = "PUT"
}

export enum PositionStatus {
    OPENING="OPENING",
    OPEN="OPEN",
    CLOSING="CLOSING",
    CLOSED="CLOSED"
}
    
export enum OrderStatus {
    WORKING = "WORKING",
    REJECTED = "REJECTED",
    PENDING_MODIFICATION = "PENDING_MODIFICATION",
    PARTIALLY_FILLED = "PARTIALLY_FILLED",
    FILLED = "FILLED",
    PENDING_CANCEL = "PENDING_CANCEL",
    CANCELLED = "CANCELLED",
    EXPIRED = "EXPIRED",
    AWAITING_FILL_DATA = "AWAITING_FILL_DATA"
}

export enum OrderSide {
    BUY = "BUY",
    SELL = "SELL"
}

export enum OrderType {
    /** Executes immediately at bid/ask */
    MARKET = "MARKET",
    /** Contains stoploss and/or takeprofit triggers */
    FLEX = "FLEX",
}

export enum TimeInForce {
    DAY = "DAY",
    GTC = "GTC"
}

export enum PriceReference {
    PERCENT = "PERCENT",
    CONTRACT = "CONTRACT",
    STOCK = "STOCK"
}

//#endregion